import {
  useDebugValue,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';

import { useAuth } from 'react-oidc-context';
import { Modal } from 'react-bootstrap';
import { Button, CircularProgress } from '@mui/material';

import {
  getStripePublishableKey,
} from '../../types/stripe';

const STRIPE_PUBLISHABLE_KEY = await getStripePublishableKey();

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ clientSecret, reloadHandler, closeModalHandler }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [
    message,
    setMessage,
  ] = useState('');
  const [
    isProcessing,
    setIsProcessing,
  ] = useState(false);

  const [
    isPaymentSucceded,
    setIsPlaymentSuceeded,
  ] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setMessage('');

    // ✅ Handle SetupIntent (Saving Payment Method)
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    console.log(error, setupIntent);

    if (error) {
      setMessage(error.message || 'Failed to save payment details.');
      setIsProcessing(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      setMessage('✅ Payment details saved! Subscription will be charged at the end of the month.');

      setTimeout(() => {
        reloadHandler();
        closeModalHandler();
        setIsProcessing(false);
      }, 2000);
    } else {
      setMessage('⚠️ Payment method setup incomplete.');
    }

    setMessage('✅ Payment Details Update Successful!');
  };

  return (
    <form onSubmit={handleSubmit} style={{ textAlign: 'center', color: 'rgb(0, 15, 30, 0.9)' }}>
      <div style={{ width: '100%', borderRadius: '0.5vw' }}>
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>

      { !isPaymentSucceded && (
        <div style={{ marginTop: '2vw', marginBottom: '1vw' }}>
          {!isProcessing
            ? (
                <Button
                  style={{
                    borderColor: 'rgb(255, 255, 255, 1)', color: 'rgb(255, 255, 255, 0.9)', padding: '0.7vw', marginBottom: '1vw', border: 'none', backgroundImage: 'linear-gradient(rgb(255, 255, 255, 0.05), rgb(10, 10, 10, 0.05)),linear-gradient(#1c6c6c, #1c6c6c)', borderRadius: '0.5vw', textTransform: 'none', fontSize: '1vw', fontWeight: 500, letterSpacing: '0.08vw',
                    width: '100%', marginTop: '1vw' }}
                  type="submit"
                  disabled={!stripe || isProcessing}
                >
                  Save
                </Button>
              )
            : <CircularProgress style={{ color: 'rgb(28, 108, 108)', marginBottom: '1vw' }} />}
        </div>
      )}

      { isPaymentSucceded
        && <div style={{ marginTop: '2vw', marginBottom: '1vw' }}>Success</div>}
    </form>
  );
};

export default function SetupPayment({ show, closeHandler, reloadHandler }): React.ReactNode {
  const [
    isProcessing,
    setIsProcessing,
  ] = useState(false);

  const auth = useAuth();

  const [
    clientSecret,
    setClientSecret,
  ] = useState('');

  const [
    sessionExpired,
    setSessionExpired,
  ] = useState(false);

  // ✅ Fetch clientSecret when user clicks "Subscribe"
  const createPaymentMethod = async () => {
    fetch(`${window.origin}/api/stripe/payment-method`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${auth.user?.access_token}`,
      },
    }).then((response) => {
      if (response.status === 403) {
        setSessionExpired(true);
        throw new Error('Session expired');
      }
      return response;
    }).then(resp => resp.json()).then((data) => {
      console.log(data);

      setClientSecret(data.clientSecret);
    }).catch((error) => {
      console.error(error);
    });
  };

  useEffect(() => {
    createPaymentMethod();
  }, []);

  const appearance = {
    theme: 'flat', // Choose: "stripe" (default), "night", "flat", "none"
    variables: {
      colorPrimary: 'rgb(0, 15, 30, 0.9)', // Adjust main brand color
      colorText: 'rgb(0, 15, 30, 0.9)', // Text color
      borderRadius: '8px', // Roundness of fields
      fontSizeBase: '16px', // Default font size
    },
    rules: {
      '.Input, .StripeElement': {
        padding: '12px',
        backgroundColor: 'transparent', // ✅ Makes input blend with background
        color: 'rgb(0, 15, 30, 0.9)',
        boxShadow: 'none', // ✅ Removes any border effect
        border: 'none', // ✅ Ensures no border is applied
      },
      '.Input:focus, .StripeElement--focus': {
        boxShadow: 'none', // ✅ No border on focus
        border: 'none',
      },
      '.Tab, .Tab:hover': {
        color: '#ff6600',
      },
      '.Label': {
        color: 'rgb(0, 15, 30, 0.9)',
      },
    },
  };

  return (
    <>
      <Modal
        show={show}
        onHide={closeHandler}
        dialogClassName="modal-50w"
        aria-labelledby="example-custom-modal-styling-title"
      >

        <Modal.Body style={{ padding: '2vw 3vw', paddingBottom: '0vw', textAlign: 'center', color: 'rgb(0, 15, 30, 0.8)' }}>
          <h4 style={{ textAlign: 'center', fontSize: '1.3vw', marginBottom: '2vw', fontWeight: '600' }}>New Payment Method</h4>
          <div style={{ textAlign: 'center', padding: '0.2rem' }}>
            { clientSecret.length > 0 && (
              <Elements stripe={stripePromise} options={{ clientSecret: clientSecret, appearance }}>
                <CheckoutForm reloadHandler={reloadHandler} closeModalHandler={closeHandler} clientSecret={clientSecret} />
              </Elements>
            )}
          </div>

        </Modal.Body>
      </Modal>

    </>
  );
}

